import React, { useState } from 'react';
import { db } from '../config/firebase';
import { doc, setDoc, arrayUnion } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { PE_LOGO_SRC } from '../constants/brandAssets';

// --- ICONS FÜR DAS UI ---
const LoaderIcon = ({ size = 24, className = "" }: any) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
);
const CheckCircleIcon = ({ size = 24, className = "" }: any) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
        <path d="m9 11 3 3L22 4" />
    </svg>
);
const AlertTriangleIcon = ({ size = 24, className = "" }: any) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
);

const NewsletterOptInPage: React.FC = () => {
    const [email, setEmail] = useState('');
    const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
    const [errorMsg, setErrorMsg] = useState('');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const cleanEmail = email.trim().toLowerCase();

        if (!cleanEmail || !cleanEmail.includes('@')) {
            setErrorMsg('Bitte gib eine gültige E-Mail Adresse ein.');
            setStatus('error');
            return;
        }

        setStatus('loading');
        setErrorMsg('');

        try {
            const docRef = doc(db, 'newsletter_system', 'global_contacts');
            
            await setDoc(docRef, {
                externalLeads: arrayUnion(cleanEmail)
            }, { merge: true });
            
            // 💉 CHIRURGIE-FIX: Trigger die neue Cloud-Function für die Willkommens-Mail
            try {
                const functionsInstance = getFunctions(db.app, 'europe-west1');
                const sendWelcomeFn = httpsCallable(functionsInstance, 'sendWelcomeMail');
                await sendWelcomeFn({ 
                    email: cleanEmail,
                    appBaseUrl: window.location.origin 
                });
            } catch (mailError) {
                // Silent Fail: Wenn Brevo hängt, zeigen wir dem Kunden trotzdem das grüne Erfolgs-UI für den Eintrag.
                console.error("Welcome Mail Error (Silent):", mailError);
            }

            setStatus('success');
        } catch (error: any) {
            console.error("Opt-In Error:", error);
            setErrorMsg(`FEHLER: ${error.message || 'Ein unerwarteter Fehler ist aufgetreten.'}`);
            setStatus('error');
        }
    };

    return (
        <div className="min-h-screen bg-[#08090b] flex items-center justify-center p-4 font-sans">
            <div className="bg-[#15171e] border border-slate-800 rounded-2xl p-8 max-w-md w-full shadow-2xl relative overflow-hidden animate-fade-in">
                
                {/* Deko-Element */}
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#E6C299] via-yellow-500 to-[#E6C299] opacity-50"></div>

                <div className="text-center flex flex-col items-center">
                    
                    {status === 'idle' || status === 'error' ? (
                        <>
                            <div className="flex justify-center mb-6">
                                <img src={PE_LOGO_SRC} alt="PreisExpert Logo" className="h-16 w-auto object-contain drop-shadow-xl" />
                            </div>
                            <h1 className="text-3xl font-black tracking-widest mb-2">
                                <span className="text-white">Mehr als ein Newsletter.</span>
                            </h1>
                            <p className="text-slate-400 text-sm mb-8 leading-relaxed">
                                Erhalte regelmäßig Impulse, die deinen Salon erfolgreicher machen.
                                <br /><br />
                                Von Preisgestaltung über Mitarbeiterführung bis hin zu Marketing, Social Media und Unternehmenszahlen - wir teilen unser Wissen aus über 200 betreuten Salons, damit du schneller vorankommst.
                                <br /><br />
                                Kostenlos. Praxisnah. Ehrlich.
                            </p>

                            <form onSubmit={handleSubmit} className="w-full flex flex-col gap-4">
                                <div className="relative">
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="deine@email.de"
                                        className="w-full bg-[#08090b] border border-slate-700 text-white px-4 py-4 rounded-xl focus:outline-none focus:border-[#E6C299]/50 transition-colors placeholder-slate-600 text-center text-lg"
                                        required
                                    />
                                </div>
                                
                                {status === 'error' && (
                                    <div className="flex items-center justify-center gap-2 text-red-400 text-xs font-bold uppercase tracking-wider bg-red-900/10 py-2 rounded-lg border border-red-900/30">
                                        <AlertTriangleIcon size={14} />
                                        {errorMsg}
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    className="w-full bg-[#E6C299] hover:bg-[#dcb07b] text-[#08090b] font-black uppercase tracking-widest text-sm py-4 rounded-xl transition-all shadow-[0_0_20px_rgba(230,194,153,0.15)] hover:shadow-[0_0_30px_rgba(230,194,153,0.3)] mt-2"
                                >
                                    Kostenlos anmelden
                                </button>
                            </form>
                            <div className="mt-6 text-xs text-slate-500 font-mono">
                                Deine Daten sind sicher. Abmeldung ist jederzeit möglich.
                            </div>
                        </>
                    ) : status === 'loading' ? (
                        <>
                            <div className="w-20 h-20 bg-[#E6C299]/10 rounded-full flex items-center justify-center mb-6 border border-[#E6C299]/30">
                                <LoaderIcon size={32} className="text-[#E6C299] animate-spin" />
                            </div>
                            <h1 className="text-2xl font-black text-white uppercase tracking-widest mb-2">Wird gespeichert...</h1>
                            <p className="text-slate-400 text-sm">Bitte warte einen Moment.</p>
                        </>
                    ) : (
                        <>
                            <div className="w-20 h-20 bg-emerald-900/20 rounded-full flex items-center justify-center mb-6 border border-emerald-500/30 shadow-[0_0_30px_rgba(16,185,129,0.2)] animate-scale-in">
                                <CheckCircleIcon size={40} className="text-emerald-500" />
                            </div>
                            <h1 className="text-2xl font-black text-white uppercase tracking-widest mb-2">Erfolgreich!</h1>
                            <p className="text-slate-400 text-sm mb-6">
                                Die E-Mail Adresse <strong className="text-white">{email}</strong> wurde sicher in unseren Verteiler aufgenommen.
                            </p>
                            <div className="text-xs text-emerald-400/70 font-mono bg-emerald-900/10 px-3 py-2 rounded-lg border border-emerald-800/30">
                                Du erhältst in Kürze unsere Benachrichtigungen.
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default NewsletterOptInPage;